import {
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react"
import { t } from "../i18n"

interface TrixEditorWrapperProps {
  value: string
  onChange: (value: string) => void
  onSelectionChange?: (activeAttributes: ActiveAttributes) => void
  placeholder?: string
  disabled?: boolean
  id?: string
}

export interface ActiveAttributes {
  bold: boolean
  italic: boolean
  strike: boolean
}

export interface TrixEditorHandle {
  toggleBold: () => void
  toggleItalic: () => void
  toggleStrike: () => void
  toggleBulletList: () => void
  toggleNumberedList: () => void
  insertFile: (file: File) => void
  insertString: (text: string) => void
  getEditorElement: () => HTMLElement | null
  getActiveAttributes: () => ActiveAttributes
}

declare global {
  interface Window {
    Trix: {
      Editor: new (element: HTMLElement) => TrixEditor
    }
  }

  interface TrixEditor {
    loadHTML: (html: string) => void
    getDocument: () => { toString: () => string }
    activateAttribute: (name: string, value?: string) => void
    deactivateAttribute: (name: string) => void
    attributeIsActive: (name: string) => boolean
    insertString: (text: string) => void
    insertFile: (file: File) => void
    insertAttachment: (attachment: unknown) => void
    recordUndoEntry: (name: string) => void
    composition: {
      insertString: (text: string) => void
    }
  }

  interface HTMLElementEventMap {
    "trix-change": CustomEvent
    "trix-initialize": CustomEvent
    "trix-selection-change": CustomEvent
  }
}

type TrixElement = HTMLElement & { editor: TrixEditor }

function getTrixEditor(element: HTMLElement | null): TrixEditor | null {
  if (element) {
    return (element as TrixElement).editor
  }
  return null
}

let idCounter = 0
function generateStableId(prefix: string): string {
  idCounter += 1
  return `${prefix}-${idCounter}`
}

export const TrixEditorWrapper = forwardRef<
  TrixEditorHandle,
  TrixEditorWrapperProps
>(function TrixEditorWrapper(
  { value, onChange, onSelectionChange, placeholder, disabled = false, id },
  ref,
) {
  const editorRef = useRef<HTMLElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const isInitializedRef = useRef(false)
  const lastValueRef = useRef(value)
  const stableIdRef = useRef<string | null>(null)
  const onSelectionChangeRef = useRef(onSelectionChange)

  // Keep ref updated with latest callback
  onSelectionChangeRef.current = onSelectionChange

  // Generate stable IDs once on first render
  if (stableIdRef.current === null) {
    stableIdRef.current = id || generateStableId("trix-editor")
  }

  const getEditor = useCallback((): TrixEditor | null => {
    return getTrixEditor(editorRef.current)
  }, [])

  const getActiveAttributes = useCallback((): ActiveAttributes => {
    const editor = getEditor()
    if (editor) {
      return {
        bold: editor.attributeIsActive("bold"),
        italic: editor.attributeIsActive("italic"),
        strike: editor.attributeIsActive("strike"),
      }
    }
    return { bold: false, italic: false, strike: false }
  }, [getEditor])

  const notifyAttributeChange = useCallback(() => {
    if (onSelectionChangeRef.current) {
      onSelectionChangeRef.current(getActiveAttributes())
    }
  }, [getActiveAttributes])

  useImperativeHandle(
    ref,
    () => ({
      toggleBold: () => {
        const editor = getEditor()
        if (editor) {
          if (editor.attributeIsActive("bold")) {
            editor.deactivateAttribute("bold")
          } else {
            editor.activateAttribute("bold")
          }
          notifyAttributeChange()
        }
      },
      toggleItalic: () => {
        const editor = getEditor()
        if (editor) {
          if (editor.attributeIsActive("italic")) {
            editor.deactivateAttribute("italic")
          } else {
            editor.activateAttribute("italic")
          }
          notifyAttributeChange()
        }
      },
      toggleStrike: () => {
        const editor = getEditor()
        if (editor) {
          if (editor.attributeIsActive("strike")) {
            editor.deactivateAttribute("strike")
          } else {
            editor.activateAttribute("strike")
          }
          notifyAttributeChange()
        }
      },
      toggleBulletList: () => {
        const editor = getEditor()
        if (editor) {
          if (editor.attributeIsActive("bullet")) {
            editor.deactivateAttribute("bullet")
          } else {
            editor.activateAttribute("bullet")
          }
        }
      },
      toggleNumberedList: () => {
        const editor = getEditor()
        if (editor) {
          if (editor.attributeIsActive("number")) {
            editor.deactivateAttribute("number")
          } else {
            editor.activateAttribute("number")
          }
        }
      },
      insertFile: (file: File) => {
        const editor = getEditor()
        if (editor) {
          editor.insertFile(file)
        }
      },
      insertString: (text: string) => {
        const editor = getEditor()
        if (editor) {
          editor.insertString(text)
        }
      },
      getEditorElement: () => editorRef.current,
      getActiveAttributes,
    }),
    [getEditor, getActiveAttributes, notifyAttributeChange],
  )

  const handleTrixChange = useCallback(() => {
    if (inputRef.current) {
      const newValue = inputRef.current.value
      if (newValue !== lastValueRef.current) {
        lastValueRef.current = newValue
        onChange(newValue)
      }
    }
  }, [onChange])

  const handleTrixInitialize = useCallback(() => {
    if (editorRef.current && inputRef.current && !isInitializedRef.current) {
      isInitializedRef.current = true
      inputRef.current.value = value
      const trixEditor = getTrixEditor(editorRef.current)
      if (trixEditor) {
        trixEditor.loadHTML(value)
      }
    }
  }, [value])

  const handleSelectionChange = useCallback(() => {
    if (onSelectionChange) {
      onSelectionChange(getActiveAttributes())
    }
  }, [onSelectionChange, getActiveAttributes])

  useEffect(() => {
    const editor = editorRef.current
    if (editor) {
      editor.addEventListener("trix-change", handleTrixChange)
      editor.addEventListener("trix-initialize", handleTrixInitialize)
      editor.addEventListener("trix-selection-change", handleSelectionChange)

      return () => {
        editor.removeEventListener("trix-change", handleTrixChange)
        editor.removeEventListener("trix-initialize", handleTrixInitialize)
        editor.removeEventListener(
          "trix-selection-change",
          handleSelectionChange,
        )
      }
    }
  }, [handleTrixChange, handleTrixInitialize, handleSelectionChange])

  useEffect(() => {
    if (
      isInitializedRef.current &&
      value !== lastValueRef.current &&
      editorRef.current
    ) {
      const trixEditor = getTrixEditor(editorRef.current)
      if (trixEditor) {
        lastValueRef.current = value
        trixEditor.loadHTML(value)
      }
    }
  }, [value])

  const editorId = stableIdRef.current!
  const inputId = `${editorId}-input`

  return (
    <div className="trix-editor-wrapper">
      <input
        ref={inputRef}
        type="hidden"
        id={inputId}
        defaultValue={value}
      />
      <trix-editor
        ref={editorRef as React.RefObject<HTMLElement>}
        id={editorId}
        input={inputId}
        placeholder={
          placeholder || t("js.campaigns.content.compose_email_placeholder")
        }
        className="trix-editor"
        {...(disabled ? { contentEditable: false } : {})}
      />
    </div>
  )
})

declare global {
  namespace JSX {
    interface IntrinsicElements {
      "trix-editor": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          input?: string
          placeholder?: string
        },
        HTMLElement
      >
    }
  }
}
